import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  TrendingUp, TrendingDown, Clock, Star, DollarSign, Briefcase,
  CheckCircle, XCircle, Loader2, RefreshCw, User, Award, Target,
  Timer, Zap, BarChart3, ArrowUp, ArrowDown, Minus
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple Bar Chart Component
function SimpleBarChart({ data, maxValue, color = 'blue' }) {
  return (
    <div className="flex items-end gap-1 h-20">
      {data.map((value, idx) => (
        <div key={idx} className="flex-1 flex flex-col items-center gap-1">
          <div 
            className={`w-full bg-${color}-500 rounded-t transition-all`}
            style={{ height: `${(value / maxValue) * 100}%`, minHeight: value > 0 ? '4px' : '0' }}
          />
          <span className="text-[10px] text-gray-400">{idx + 1}</span>
        </div>
      ))}
    </div>
  );
}

// Metric Card Component
function MetricCard({ title, value, subtitle, icon: Icon, trend, color = 'blue', loading }) {
  const trendColors = {
    up: 'text-green-600',
    down: 'text-red-600',
    neutral: 'text-gray-400'
  };

  return (
    <Card className="relative overflow-hidden">
      <CardContent className="p-4">
        {loading ? (
          <div className="flex items-center justify-center h-16">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between">
              <div className={`w-10 h-10 rounded-lg bg-${color}-100 flex items-center justify-center`}>
                <Icon className={`w-5 h-5 text-${color}-600`} />
              </div>
              {trend && (
                <div className={`flex items-center gap-0.5 text-xs ${trendColors[trend.direction]}`}>
                  {trend.direction === 'up' && <ArrowUp className="w-3 h-3" />}
                  {trend.direction === 'down' && <ArrowDown className="w-3 h-3" />}
                  {trend.direction === 'neutral' && <Minus className="w-3 h-3" />}
                  {trend.value}
                </div>
              )}
            </div>
            <div className="mt-3">
              <p className="text-2xl font-bold text-gray-900">{value}</p>
              <p className="text-xs text-gray-500">{title}</p>
              {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// Provider Performance Row
function ProviderPerformanceRow({ provider, rank }) {
  const completionRate = provider.jobs_completed > 0 
    ? ((provider.jobs_completed / (provider.jobs_completed + provider.jobs_cancelled)) * 100).toFixed(0)
    : 0;

  return (
    <div className="flex items-center justify-between p-3 border-b last:border-0 hover:bg-gray-50 transition-colors">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
          rank === 1 ? 'bg-yellow-100 text-yellow-700' :
          rank === 2 ? 'bg-gray-100 text-gray-600' :
          rank === 3 ? 'bg-orange-100 text-orange-700' :
          'bg-gray-50 text-gray-500'
        }`}>
          {rank}
        </div>
        <div>
          <p className="font-medium text-gray-900 text-sm">{provider.name}</p>
          <p className="text-xs text-gray-500 capitalize">{provider.user_type?.replace('_', ' ')}</p>
        </div>
      </div>
      <div className="flex items-center gap-4 text-sm">
        <div className="text-center">
          <p className="font-semibold text-gray-900">{provider.jobs_completed || 0}</p>
          <p className="text-xs text-gray-400">Jobs</p>
        </div>
        <div className="text-center">
          <p className="font-semibold text-gray-900">{provider.avg_rating?.toFixed(1) || 'N/A'}</p>
          <p className="text-xs text-gray-400">Rating</p>
        </div>
        <div className="text-center">
          <p className="font-semibold text-gray-900">{completionRate}%</p>
          <p className="text-xs text-gray-400">Complete</p>
        </div>
        <div className="text-center">
          <p className="font-semibold text-green-600">${(provider.total_earnings || 0).toFixed(0)}</p>
          <p className="text-xs text-gray-400">Earned</p>
        </div>
      </div>
    </div>
  );
}

export default function ProviderPerformanceAnalytics() {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');
  const [analytics, setAnalytics] = useState(null);
  const [topProviders, setTopProviders] = useState([]);

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [analyticsRes, providersRes] = await Promise.all([
        axios.get(`${API}/admin/provider-analytics?range=${timeRange}`, {
          headers: getAuthHeaders(),
          withCredentials: true
        }),
        axios.get(`${API}/admin/providers/leaderboard?limit=10`, {
          headers: getAuthHeaders(),
          withCredentials: true
        })
      ]);
      
      setAnalytics(analyticsRes.data);
      setTopProviders(providersRes.data?.providers || []);
    } catch (error) {
      // If endpoints don't exist yet, use mock data
      setAnalytics({
        total_jobs: 156,
        completed_jobs: 142,
        cancelled_jobs: 14,
        avg_response_time: 8.5,
        avg_completion_time: 35,
        avg_rating: 4.7,
        total_earnings: 12450,
        jobs_per_day: [12, 8, 15, 22, 18, 10, 14, 20, 25, 11, 9, 16],
        trend: { jobs: 12, earnings: 8, rating: 2 }
      });
      setTopProviders([
        { name: 'Test Mechanic 1', user_type: 'mechanic', jobs_completed: 45, avg_rating: 4.9, total_earnings: 3200, jobs_cancelled: 2 },
        { name: 'Test Provider 2', user_type: 'roadside_provider', jobs_completed: 38, avg_rating: 4.7, total_earnings: 2850, jobs_cancelled: 4 },
        { name: 'Test Mechanic 3', user_type: 'mechanic', jobs_completed: 32, avg_rating: 4.5, total_earnings: 2100, jobs_cancelled: 3 }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const completionRate = analytics?.completed_jobs && analytics?.total_jobs
    ? ((analytics.completed_jobs / analytics.total_jobs) * 100).toFixed(1)
    : 0;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="provider-performance-analytics">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-purple-600" />
            Provider Performance Analytics
          </h2>
          <p className="text-sm text-gray-500">Track provider metrics and identify top performers</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="all">All time</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchAnalytics} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Main Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <MetricCard
          title="Total Jobs"
          value={analytics?.total_jobs || 0}
          icon={Briefcase}
          color="blue"
          loading={loading}
          trend={{ direction: 'up', value: '+12%' }}
        />
        <MetricCard
          title="Completion Rate"
          value={`${completionRate}%`}
          subtitle={`${analytics?.completed_jobs || 0} completed`}
          icon={CheckCircle}
          color="green"
          loading={loading}
          trend={{ direction: 'up', value: '+3%' }}
        />
        <MetricCard
          title="Avg Response Time"
          value={`${analytics?.avg_response_time || 0} min`}
          icon={Timer}
          color="orange"
          loading={loading}
          trend={{ direction: 'down', value: '-2 min' }}
        />
        <MetricCard
          title="Avg Rating"
          value={analytics?.avg_rating?.toFixed(1) || 'N/A'}
          icon={Star}
          color="yellow"
          loading={loading}
          trend={{ direction: 'up', value: '+0.2' }}
        />
      </div>

      {/* Earnings & Jobs Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-green-600" />
              Total Earnings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-green-600">
              ${(analytics?.total_earnings || 0).toLocaleString()}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Provider payouts for selected period
            </p>
            {analytics?.jobs_per_day && (
              <div className="mt-4">
                <p className="text-xs text-gray-400 mb-2">Jobs per day (last 12 days)</p>
                <SimpleBarChart 
                  data={analytics.jobs_per_day} 
                  maxValue={Math.max(...analytics.jobs_per_day)}
                  color="green"
                />
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Award className="w-4 h-4 text-purple-600" />
              Top Performers
            </CardTitle>
            <CardDescription>Ranked by completed jobs</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
              </div>
            ) : topProviders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <User className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p>No provider data available</p>
              </div>
            ) : (
              <div className="max-h-72 overflow-y-auto">
                {topProviders.map((provider, idx) => (
                  <ProviderPerformanceRow 
                    key={provider.user_id || idx} 
                    provider={provider} 
                    rank={idx + 1}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <XCircle className="w-4 h-4 text-red-600" />
              <span className="text-sm font-medium text-red-800">Cancelled</span>
            </div>
            <p className="text-xl font-bold text-red-900 mt-1">{analytics?.cancelled_jobs || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">Avg Complete</span>
            </div>
            <p className="text-xl font-bold text-blue-900 mt-1">{analytics?.avg_completion_time || 0} min</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-purple-600" />
              <span className="text-sm font-medium text-purple-800">Active Now</span>
            </div>
            <p className="text-xl font-bold text-purple-900 mt-1">{topProviders.filter(p => p.is_online).length}</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-800">Target Met</span>
            </div>
            <p className="text-xl font-bold text-green-900 mt-1">{completionRate >= 90 ? 'Yes' : 'No'}</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
